import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Search, AlertTriangle, Trash2, Loader2, CheckCircle2, Info, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrokerCleanupTab() {
  const [ibs, setIbs] = useState([]);
  const [selectedIb, setSelectedIb] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Selection: { 'YYYY-MM__brokerName': true }
  const [selected, setSelected] = useState({});

  // Dedup state
  const [dedupRunning, setDedupRunning] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);

  // Slot archive / key rename state
  const [archiveSlot, setArchiveSlot] = useState('');
  const [archivePreview, setArchivePreview] = useState(null);
  const [renameFrom, setRenameFrom] = useState('');
  const [renameTo, setRenameTo] = useState('');
  const [renamePreview, setRenamePreview] = useState(null);
  const [slotBusy, setSlotBusy] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [monthAudit, setMonthAudit] = useState(null);
  const [monthFix, setMonthFix] = useState(null);
  const [dataHistory, setDataHistory] = useState([]);
  const [batches, setBatches] = useState([]);
  const [hideMirrors, setHideMirrors] = useState(true);
  const [breakdownMonth, setBreakdownMonth] = useState('2026-05');
  const [breakdown, setBreakdown] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [dateFix, setDateFix] = useState(null);

  const runAnomalyScan = async () => {
    setSlotBusy(true);
    try {
      const res = await axios.get(`${API}/admin/data-anomalies?year=2026${selectedIb ? `&ib_id=${selectedIb}` : ''}`,
        { headers: authHeader() });
      setAnomalies(res.data);
      if ((res.data.total || 0) === 0) toast.success('Tiada angka pelik dijumpai');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal scan');
    } finally { setSlotBusy(false); }
  };

  const runDateFix = async (dry) => {
    if (!selectedIb) { toast.error('Pilih channel dulu'); return; }
    if (!dry) {
      const typed = window.prompt(
        'Betulkan tarikh rosak (contoh 0001-07-01 → 2026-07-01)?\n\n' +
        'Snapshot penuh disimpan dulu, jadi boleh undo.\n\nTaip BETUL untuk teruskan:');
      if (typed !== 'BETUL') { toast.info('Dibatalkan'); return; }
    }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/fix-entry-dates/${selectedIb}`, { dry_run: dry },
        { headers: authHeader() });
      setDateFix(res.data);
      if (!dry) {
        toast.success(`${res.data.fixes} tarikh dibetulkan`);
        await runAnomalyScan();
      } else if ((res.data.fixes || 0) === 0) {
        toast.info('Tiada tarikh rosak untuk channel ni');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal betulkan tarikh');
    } finally { setSlotBusy(false); }
  };


  const undoLastBatch = async (operation) => {
    const typed = window.prompt(
      `Undo operasi "${operation}" yang terakhir untuk channel ni?\n\n` +
      `Semua dokumen bulan yang terlibat akan dipulangkan kepada keadaan SEBELUM operasi tu ` +
      `(dari snapshot penuh).\n\nTaip UNDO untuk teruskan:`
    );
    if (typed !== 'UNDO') { toast.info('Dibatalkan'); return; }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/undo-last-batch/${selectedIb}`,
        { operation }, { headers: authHeader() });
      toast.success(`Undo berjaya — ${res.data.restored_months.length} bulan dipulangkan (${res.data.restored_months.join(', ')})`);
      setSyncPreview(null);
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo gagal');
    } finally { setSlotBusy(false); }
  };

  const restoreOriginal = async () => {
    const typed = window.prompt(
      `Pulangkan channel ni ke KEADAAN ASAL?\n\n` +
      `Setiap bulan akan dipulangkan kepada snapshot PALING AWAL yang ada — iaitu sebelum ` +
      `sebarang sync / pembetulan bulan / archive dijalankan.\n\nTaip ASAL untuk teruskan:`
    );
    if (typed !== 'ASAL') { toast.info('Dibatalkan'); return; }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/restore-original/${selectedIb}`, {}, { headers: authHeader() });
      toast.success(`${res.data.months} bulan dipulangkan ke keadaan asal`);
      setSyncPreview(null);
      setMonthFix(null);
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore gagal');
    } finally { setSlotBusy(false); }
  };

  const fetchBatches = async () => {
    try {
      const res = await axios.get(`${API}/admin/cleanup-batches/${selectedIb}`, { headers: authHeader() });
      setBatches(res.data.batches || []);
      if ((res.data.batches || []).length === 0) toast.info('Tiada snapshot untuk channel ni');
    } catch (e) {
      toast.error('Gagal ambil senarai snapshot');
    }
  };

  const restoreSpecificBatch = async (b) => {
    const typed = window.prompt(
      `Restore snapshot ${b.operation} (${String(b.at).slice(0, 16).replace('T', ' ')})?\n\n` +
      `Bulan: ${b.months.join(', ')}\n\nTaip RESTORE untuk teruskan:`
    );
    if (typed !== 'RESTORE') { toast.info('Dibatalkan'); return; }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/broker-restore/${b.batch_id}`, {}, { headers: authHeader() });
      toast.success(`Restored ${res.data.restored_months.length} bulan`);
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore gagal');
    } finally { setSlotBusy(false); }
  };

  const runMonthAudit = async () => {
    setSlotBusy(true);
    try {
      const res = await axios.get(`${API}/admin/daily-data-month-audit/${selectedIb}`, { headers: authHeader() });
      setMonthAudit(res.data);
      toast.info(res.data.total_misplaced_entries === 0
        ? 'Semua entry berada dalam bulan yang betul'
        : `${res.data.total_misplaced_entries} entries dalam bulan salah`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit gagal');
    } finally { setSlotBusy(false); }
  };

  const runMonthFix = async (dryRun) => {
    if (!dryRun) {
      const typed = window.prompt(
        `Pindahkan ${monthFix?.moves || 0} entries ke bulan yang betul?\n\n` +
        `Tiada data dipadam — snapshot penuh disimpan dan boleh restore dari Cleanup History.\n\nTaip FIX untuk teruskan:`
      );
      if (typed !== 'FIX') { toast.info('Dibatalkan'); return; }
    }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/daily-data-fix-months/${selectedIb}`,
        { dry_run: dryRun }, { headers: authHeader() });
      if (dryRun) {
        setMonthFix(res.data);
        toast.info(res.data.moves === 0 ? 'Tiada entry perlu dipindah' : `${res.data.moves} entries boleh dipindah`);
      } else {
        setMonthFix(null);
        toast.success(`${res.data.moves} entries dipindah ke bulan betul`);
        await runMonthAudit();
        await runDiagnostic();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Betulkan gagal');
    } finally { setSlotBusy(false); }
  };

  const fetchDataHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/data-history/${selectedIb}`, { headers: authHeader() });
      setDataHistory(res.data.events || []);
      if ((res.data.events || []).length === 0) toast.info('Tiada rekod import/recovery untuk channel ni');
    } catch (e) {
      toast.error('Gagal ambil sejarah');
    }
  };
  const [configHistory, setConfigHistory] = useState([]);

  const runSyncEntries = async (dryRun) => {
    if (!dryRun) {
      const typed = window.prompt(
        `Selaraskan data broker untuk ${diagnostic?.ib_name || 'channel ni'}?\n\n` +
        `Tiada data dipadam — angka slot Daily Data akan jadi sumber rujukan untuk semua page.\n` +
        `Snapshot penuh disimpan dan boleh restore dari History.\n\nTaip SYNC untuk teruskan:`
      );
      if (typed !== 'SYNC') { toast.info('Dibatalkan'); return; }
    }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/broker-sync-entries/${selectedIb}`,
        { dry_run: dryRun }, { headers: authHeader() });
      if (dryRun) {
        setSyncPreview(res.data);
        toast.info(res.data.total_entries_changed === 0
          ? 'Semua data dah selaras — tiada perubahan diperlukan'
          : `${res.data.total_entries_changed} entries perlu diselaraskan`);
      } else {
        setSyncPreview(null);
        toast.success(`${res.data.total_entries_changed} entries diselaraskan`);
        await runDiagnostic();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync gagal');
    } finally { setSlotBusy(false); }
  };

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const runMonthBreakdown = async () => {    if (!/^\d{4}-\d{2}$/.test(breakdownMonth)) { toast.error('Format bulan: YYYY-MM'); return; }
    setSlotBusy(true);
    try {
      const res = await axios.get(`${API}/admin/month-breakdown/${breakdownMonth}`, { headers: authHeader() });
      setBreakdown(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal ambil breakdown');
    } finally { setSlotBusy(false); }
  };

  const runSlotArchive = async (dryRun) => {
    if (!dryRun) {
      const typed = window.prompt(
        `Archive ${archiveSlot} untuk ${diagnostic?.ib_name || 'channel ni'}?\n\n` +
        `Data TIDAK dipadam — ia dipindah ke archived_${archiveSlot} dan boleh dibawa balik dengan "Undo Archive".\n` +
        `Full snapshot juga disimpan.\n\nTaip ARCHIVE untuk teruskan:`
      );
      if (typed !== 'ARCHIVE') { toast.info('Dibatalkan'); return; }
    }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/broker-slot-archive/${selectedIb}`,
        { slot: archiveSlot, dry_run: dryRun }, { headers: authHeader() });
      if (dryRun) {
        setArchivePreview(res.data);
        toast.info(`${res.data.total_entries} entries dijumpai dalam ${archiveSlot}`);
      } else {
        setArchivePreview(null);
        toast.success(`${res.data.moved_entries} entries diarchive dari ${archiveSlot}`);
        await runDiagnostic();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive gagal');
    } finally { setSlotBusy(false); }
  };

  const runSlotUnarchive = async () => {
    if (!window.confirm(`Bawa balik data archived_${archiveSlot} ke ${archiveSlot}?`)) return;
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/broker-slot-unarchive/${selectedIb}`,
        { slot: archiveSlot }, { headers: authHeader() });
      toast.success(`${res.data.restored_entries} entries dibawa balik ke ${archiveSlot}`);
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo gagal');
    } finally { setSlotBusy(false); }
  };

  const runKeyRename = async (dryRun) => {
    if (!dryRun) {
      const typed = window.prompt(
        `Pindahkan data "${renameFrom}" → "${renameTo}"?\n\n` +
        `Nilai akan dicampur (additive) kalau nama destinasi dah ada. Full snapshot disimpan.\n\nTaip RENAME untuk teruskan:`
      );
      if (typed !== 'RENAME') { toast.info('Dibatalkan'); return; }
    }
    setSlotBusy(true);
    try {
      const res = await axios.post(`${API}/admin/broker-key-rename/${selectedIb}`,
        { from_key: renameFrom, to_key: renameTo, dry_run: dryRun }, { headers: authHeader() });
      if (dryRun) {
        setRenamePreview(res.data);
        toast.info(`${res.data.total_entries} entries dijumpai untuk "${renameFrom}"`);
      } else {
        setRenamePreview(null);
        toast.success(`${res.data.moved_entries} entries dipindahkan`);
        await runDiagnostic();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename gagal');
    } finally { setSlotBusy(false); }
  };

  const fetchConfigHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/broker-config-history/${selectedIb}`, { headers: authHeader() });
      setConfigHistory(res.data.changes || []);
      if ((res.data.changes || []).length === 0) toast.info('Tiada rekod tukar nama untuk channel ni');
    } catch (e) {
      toast.error('Gagal ambil sejarah');
    }
  };

  const runDedup = async (dryRun) => {
    setDedupRunning(true);
    setDedupResult(null);
    try {
      const token = localStorage.getItem('token');
      const body = { dry_run: dryRun, keep_strategy: 'richest' };
      if (selectedIb) body.ib_id = selectedIb;
      const res = await axios.post(`${API}/admin/daily-data/dedup-cleanup`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDedupResult(res.data);
      const n = res.data.total_duplicate_entries_removed;
      if (dryRun) {
        toast.info(`Scan: ${n} duplicate entries dijumpai across ${res.data.modified_docs} docs`);
      } else {
        toast.success(`Cleanup: ${n} duplicates dipadam dari ${res.data.modified_docs} docs`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dedup failed (must be workspace owner)');
    } finally {
      setDedupRunning(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/ib/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setIbs((res.data?.ibs || res.data || []).map(ib => ({ id: ib.id, name: ib.name }))))
      .catch(() => {});
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/broker-cleanup/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch (e) {
      toast.error('Failed to fetch history');
    }
  };

  const restoreBatch = async (log) => {
    if (!log.backup_available) {
      toast.error('No backup available for this cleanup (legacy cleanup before backup feature)');
      return;
    }
    const confirmMsg = `Restore ${log.modified_entries} entries for ${log.ib_name}?\n\n` +
      `Months: ${(log.affected_months || []).join(', ')}\n` +
      `Brokers removed: ${(log.remove_brokers || []).join(', ') || '(none)'}\n` +
      `Done by: ${log.actor}\nAt: ${log.at}\n\n` +
      `Tindakan ini akan REPLACE sales data dengan backup snapshot.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/broker-restore/${log.batch_id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Restored ${res.data.restored_months.length} months`);
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const runDiagnostic = async () => {
    if (!selectedIb) { toast.error('Pilih IB dulu'); return; }
    setLoading(true);
    setSelected({});
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/broker-diagnostic/${selectedIb}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDiagnostic(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostic failed');
    } finally { setLoading(false); }
  };

  const toggleSelect = (month, brokerName) => {
    const key = `${month}__${brokerName}`;
    setSelected(s => ({ ...s, [key]: !s[key] }));
  };

  const selectAllOrphans = () => {
    const next = {};
    diagnostic?.months?.forEach(m => {
      m.brokers.forEach(b => {
        if (b.is_orphan) next[`${m.month}__${b.broker_name}`] = true;
      });
    });
    setSelected(next);
  };

  const selectAllLegacy = () => {
    const next = {};
    diagnostic?.months?.forEach(m => {
      m.brokers.forEach(b => {
        if (b.is_legacy) next[`${m.month}__${b.broker_name}`] = true;
      });
    });
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  // Build payload grouped by month
  const buildPayload = () => {
    const map = {};   // month -> { remove_brokers:[], remove_legacy_fields:[] }
    Object.keys(selected).forEach(k => {
      if (!selected[k]) return;
      const [month, ...rest] = k.split('__');
      const broker = rest.join('__');
      if (!map[month]) map[month] = { remove_brokers: [], remove_legacy_fields: [] };
      if (broker.startsWith('[legacy ')) {
        const legacyKey = broker.replace('[legacy ', '').replace(']', '');
        map[month].remove_legacy_fields.push(legacyKey);
      } else {
        map[month].remove_brokers.push(broker);
      }
    });
    return map;
  };

  const runCleanup = async () => {
    const map = buildPayload();
    const months = Object.keys(map);
    if (months.length === 0) { toast.error('Pilih sekurang-kurangnya 1 broker untuk remove'); return; }

    // Aggregate unique brokers & legacy fields across all selected months
    const remove_brokers = Array.from(new Set(months.flatMap(m => map[m].remove_brokers)));
    const remove_legacy_fields = Array.from(new Set(months.flatMap(m => map[m].remove_legacy_fields)));

    const ibName = diagnostic?.ib_name || selectedIb;
    const confirmMsg = `Confirm remove dari ${ibName}:\n\n` +
      `Months: ${months.join(', ')}\n` +
      `Brokers: ${remove_brokers.join(', ') || '(none)'}\n` +
      `Legacy fields: ${remove_legacy_fields.join(', ') || '(none)'}\n\n` +
      `Tindakan ini TIDAK boleh undo. Teruskan?`;
    if (!window.confirm(confirmMsg)) return;

    setCleaning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/broker-cleanup/${selectedIb}`, {
        months,
        remove_brokers,
        remove_legacy_fields,
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success(`Cleanup done — ${res.data.modified_entries} entries modified across ${res.data.affected_months.length} months`);
      // Re-run diagnostic
      await runDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally { setCleaning(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-[#E63946]/15 to-transparent border border-[#E63946]/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#E63946]/20 border border-[#E63946]/40 flex items-center justify-center">
            <Database size={22} className="text-[#E63946]" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Broker Data Cleanup</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">Diagnose & remove orphan broker entries dari daily_data_2026 sales — scoped per IB</p>
          </div>
        </div>
        <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#EF4444]/15 border border-[#EF4444]/40">
          <AlertTriangle size={16} className="text-[#EF4444] mt-0.5 shrink-0" />
          <div className="text-[11px] text-[#FCA5A5] space-y-1">
            <p><strong className="text-white">⚠️ CRITICAL — Read before using:</strong></p>
            <p>• Tool ini buang data broker secara KEKAL dari daily_data_2026 sales entries.</p>
            <div>• Cuma tick broker yang ditandakan <Badge className="text-[8px] bg-red-500/20 text-red-400 border-red-500/30 mx-1">ORPHAN</Badge> atau <Badge className="text-[8px] bg-purple-500/20 text-purple-400 border-purple-500/30 mx-1">LEGACY</Badge> — JANGAN tick yang <Badge className="text-[8px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mx-1">ENABLED</Badge>.</div>            <p>• Kalau &quot;ORPHAN&quot; broker rupanya data sebenar (case mismatch dgn config), <strong>JANGAN remove</strong> — pergi ke Channel Settings → Broker Config dan add broker tu ke enabled list dulu, baru data tally.</p>
            <p>• <strong className="text-[#10B981]">Auto-backup ENABLED</strong>: Setiap cleanup save full snapshot. Boleh restore via History button di bawah.</p>
          </div>
        </div>
      </div>

      {/* IB Picker */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--text-muted))]">IB:</label>
        <Select value={selectedIb} onValueChange={setSelectedIb}>
          <SelectTrigger className="w-[260px] h-9 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="cleanup-ib-selector">
            <SelectValue placeholder="Pilih IB" />
          </SelectTrigger>
          <SelectContent>
            {ibs.map(ib => <SelectItem key={ib.id} value={ib.id}>{ib.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={runDiagnostic} disabled={!selectedIb || loading} className="text-xs bg-[#3B82F6] hover:bg-[#2563EB]" data-testid="run-diagnostic-btn">
          {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Search size={12} className="mr-1.5" />}
          Run Diagnostic
        </Button>
        <Button size="sm" variant="outline" onClick={fetchHistory} className="text-xs ml-auto" data-testid="cleanup-history-btn">
          <CheckCircle2 size={12} className="mr-1.5 text-emerald-400" />
          Cleanup History & Restore
        </Button>
      </div>

      {/* Month breakdown — kenapa Yearly Snapshot bulan ni tinggi? */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[#F59E0B]/40" data-testid="month-breakdown-section">
        <h4 className="text-sm font-bold text-white mb-1">Audit Angka Yearly Snapshot (semua channel)</h4>
        <p className="text-[11px] text-[hsl(var(--text-muted))] mb-3">
          Tunjuk dari mana angka komisen bulan tu datang — per channel, termasuk berapa banyak
          datang dari &quot;top-up&quot; legacy slot dan tarikh yang muncul dalam lebih dari satu dokumen bulan.
          100% baca sahaja, tiada data disentuh.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={breakdownMonth} onChange={(e) => setBreakdownMonth(e.target.value)}
            placeholder="2026-05" data-testid="breakdown-month-input"
            className="h-9 w-[120px] px-3 rounded-lg text-xs bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white" />
          <Button size="sm" className="text-xs bg-[#F59E0B] hover:bg-[#d97706] text-black"
            disabled={slotBusy} onClick={runMonthBreakdown} data-testid="breakdown-run-btn">
            {slotBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />}
            Semak Bulan
          </Button>
        </div>
        {breakdown && (
          <div className="mt-3 space-y-2" data-testid="breakdown-result">
            <div className="text-xs text-white font-mono" data-testid="breakdown-total">
              {breakdown.month} · Total RM {breakdown.total_commission_myr?.toLocaleString()} · rate {breakdown.rate}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-[hsl(var(--text-muted))]">
                  <tr className="text-left">
                    <th className="py-1 pr-3">Channel</th>
                    <th className="py-1 pr-3">Commission RM</th>
                    <th className="py-1 pr-3">Bentuk dict</th>
                    <th className="py-1 pr-3">Bentuk legacy</th>
                    <th className="py-1 pr-3">Entries</th>
                    <th className="py-1 pr-3">Tarikh berganda</th>
                    <th className="py-1">Dokumen</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-white">
                  {(breakdown.channels || []).map((c) => (
                    <tr key={c.ib_id} className="border-t border-[hsl(var(--border))]" data-testid={`breakdown-row-${c.ib_id}`}>
                      <td className="py-1 pr-3">{c.name}</td>
                      <td className="py-1 pr-3">{c.commission_myr?.toLocaleString()}</td>
                      <td className="py-1 pr-3">{c.dict_shape_myr?.toLocaleString()}</td>
                      <td className="py-1 pr-3">{c.legacy_shape_myr?.toLocaleString()}</td>
                      <td className="py-1 pr-3">{c.entries_counted}</td>
                      <td className={`py-1 pr-3 ${(c.duplicate_dates || []).length > 0 ? 'text-[#EF4444]' : ''}`}>{(c.duplicate_dates || []).length}</td>
                      <td className="py-1">{Object.entries(c.entries_per_document || {}).map(([m, n]) => `${m}:${n}`).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Anomaly watchdog — tarikh rosak & angka pelik */}
      <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[#E63946]/40" data-testid="anomaly-section">
        <h4 className="text-sm font-bold text-white mb-1">Pengesan Tarikh Rosak &amp; Angka Pelik</h4>
        <p className="text-[11px] text-[hsl(var(--text-muted))] mb-3">
          Scan semua Daily Data 2026: tarikh di luar tahun (duit tersembunyi dari Yearly Snapshot),
          entry kosong, dan nilai yang terlalu jauh dari median channel sendiri (contoh active 17,187).
          Scan 100% baca sahaja.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="text-xs bg-[#E63946] hover:bg-[#c62828]"
            disabled={slotBusy} onClick={runAnomalyScan} data-testid="anomaly-scan-btn">
            {slotBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <AlertTriangle size={12} className="mr-1" />}
            Scan {selectedIb ? 'Channel Ni' : 'Semua Channel'}
          </Button>
          <Button size="sm" variant="outline" className="text-xs"
            disabled={!selectedIb || slotBusy} onClick={() => runDateFix(true)} data-testid="datefix-preview-btn">
            Preview Betulkan Tarikh
          </Button>
          <Button size="sm" className="text-xs bg-[#F59E0B] hover:bg-[#d97706] text-black"
            disabled={!selectedIb || slotBusy || !dateFix?.fixes} onClick={() => runDateFix(false)}
            data-testid="datefix-run-btn">
            Betulkan {dateFix?.fixes || 0} Tarikh
          </Button>
        </div>

        {dateFix && (
          <div className="mt-3 text-[11px] font-mono text-white space-y-1" data-testid="datefix-result">
            <div>{dateFix.dry_run ? 'PREVIEW' : 'SIAP'} · {dateFix.fixes} tarikh · bulan: {(dateFix.touched_months || []).join(', ') || '-'}</div>
            {(dateFix.detail || []).slice(0, 10).map((f, i) => (
              <div key={i} className="text-[hsl(var(--text-muted))]">
                {f.list} {f.month}: <span className="text-[#E63946]">{f.from}</span> → <span className="text-[#22C55E]">{f.to}</span>
              </div>
            ))}
            {(dateFix.skipped || []).map((s, i) => (
              <div key={`s${i}`} className="text-[#F59E0B]">skip {s.from} → {s.to}: {s.reason}</div>
            ))}
          </div>
        )}

        {anomalies && (
          <div className="mt-3 space-y-2" data-testid="anomaly-result">
            <div className="text-xs text-white font-mono" data-testid="anomaly-total">
              {anomalies.total} isu dijumpai{Object.keys(anomalies.counts || {}).length > 0
                ? ` · ${Object.entries(anomalies.counts).map(([k, v]) => `${k}:${v}`).join(' · ')}` : ''}
            </div>
            <div className="max-h-[320px] overflow-y-auto space-y-1">
              {(anomalies.findings || []).map((f, i) => (
                <div key={i} data-testid={`anomaly-row-${i}`}
                  className="flex items-start gap-2 text-[11px] p-2 rounded-lg bg-[hsl(var(--surface-elevated))]">
                  <Badge className={`text-[9px] shrink-0 ${f.severity === 'high' ? 'bg-[#E63946]' : 'bg-[#F59E0B] text-black'}`}>
                    {f.kind}
                  </Badge>
                  <span className="text-white font-mono shrink-0">{f.channel}</span>
                  <span className="text-[hsl(var(--text-muted))] font-mono shrink-0">{f.date}</span>
                  <span className="text-[hsl(var(--text-muted))]">{f.message}</span>
                  {f.suggested_date && <span className="text-[#22C55E] font-mono ml-auto shrink-0">→ {f.suggested_date}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cleanup History */}
      {showHistory && (
        <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]" data-testid="history-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-white">Cleanup History (Last 50)</h4>
            <Button size="sm" variant="ghost" onClick={() => setShowHistory(false)} className="text-xs">Hide</Button>
          </div>
          {history.length === 0 ? (
            <p className="text-[11px] text-[hsl(var(--text-muted))] text-center py-4">No cleanup history yet.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {history.map((log, i) => (
                <div key={i} className="p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]" data-testid={`history-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-white">{log.ib_name || log.ib_id}</span>
                        <Badge className="text-[9px] bg-red-500/15 text-red-400 border-red-500/30">{log.modified_entries} entries removed</Badge>
                        {log.backup_available
                          ? <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Backup ✓</Badge>
                          : <Badge className="text-[9px] bg-gray-500/15 text-gray-400 border-gray-500/30">No backup (legacy)</Badge>}
                      </div>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        Months: <span className="text-white">{(log.affected_months || log.months || []).join(', ')}</span>
                      </p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        Brokers: <span className="text-white">{[...(log.remove_brokers || []), ...((log.remove_legacy || []).map(l => `[legacy ${l}]`))].join(', ') || '(none)'}</span>
                      </p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))]">
                        By: <span className="text-white">{log.actor}</span> • {new Date(log.at).toLocaleString()}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => restoreBatch(log)} disabled={!log.backup_available}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" data-testid={`restore-${i}`}>
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Diagnostic Results */}
      {diagnostic && (
        <>
          <div className="p-4 rounded-xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-xs text-[hsl(var(--text-muted))]">IB</p>
                <p className="text-sm font-bold text-white">{diagnostic.ib_name}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--text-muted))]">Enabled brokers (display name)</p>
                <p className="text-sm font-mono text-emerald-400">{(diagnostic.enabled_display_names || []).filter(n => !n.startsWith('broker')).join(', ') || '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={selectAllOrphans} className="text-xs">Select all ORPHAN</Button>
                <Button size="sm" variant="outline" onClick={selectAllLegacy} className="text-xs">Select all LEGACY</Button>
                <Button size="sm" variant="outline" onClick={clearSelection} className="text-xs">Clear</Button>
              </div>
            </div>

            {(diagnostic.months || []).map(m => (
              <div key={m.month} className="mb-4 rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                <div className="px-4 py-2.5 bg-[hsl(var(--surface-elevated))] border-b border-[hsl(var(--border))] flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm font-bold text-white">{m.month}</span>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-[#3B82F6]" data-testid={`effective-${m.month}`}>
                      Yang dipakai page lain: <span className="font-mono font-bold">${fmt(m.effective_commission_usd)}</span>
                    </span>
                  </div>
                </div>
                <div className="px-4 py-2 bg-[hsl(var(--surface-elevated))]/60 border-b border-[hsl(var(--border))] flex items-center justify-end gap-3 text-[11px]">
                    <span className="text-[hsl(var(--text-muted))]">Total: <span className="text-white font-mono">${fmt(m.total_commission_usd)}</span></span>
                    <span className="text-emerald-400">Enabled: <span className="font-mono">${fmt(m.enabled_commission_usd)}</span></span>
                    {m.orphan_commission_usd > 0 && <span className="text-red-400">Orphan: <span className="font-mono">${fmt(m.orphan_commission_usd)}</span></span>}
                    {m.legacy_commission_usd > 0 && <span className="text-purple-400">Legacy: <span className="font-mono">${fmt(m.legacy_commission_usd)}</span></span>}
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--surface))]">
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="px-3 py-2 text-left w-10"></th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Broker</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">USD</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">~MYR</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Lots</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Active</th>
                      <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Entries</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.brokers.filter(b => hideMirrors ? !b.mirror_of : true).map(b => {
                      const key = `${m.month}__${b.broker_name}`;
                      const checked = !!selected[key];
                      const canRemove = b.is_orphan || b.is_legacy;
                      return (
                        <tr key={key} className={`border-b border-[hsl(var(--border))]/40 ${checked ? 'bg-red-500/8' : 'hover:bg-[hsl(var(--surface-elevated))]/40'}`}>
                          <td className="px-3 py-2 text-center">
                            {canRemove ? (
                              <input type="checkbox" checked={checked}
                                onChange={() => toggleSelect(m.month, b.broker_name)}
                                className="cursor-pointer"
                                data-testid={`cb-${m.month}-${b.broker_name}`} />
                            ) : <span className="text-emerald-500">✓</span>}
                          </td>
                          <td className="px-3 py-2 font-semibold text-white">{b.broker_name}</td>
                          <td className="px-3 py-2 text-right font-mono">${fmt(b.commission_usd)}</td>
                          <td className="px-3 py-2 text-right font-mono text-[hsl(var(--text-muted))]">RM {fmt(b.commission_myr_approx)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(b.lots)}</td>
                          <td className="px-3 py-2 text-right font-mono">{b.active}</td>
                          <td className="px-3 py-2 text-right font-mono text-[hsl(var(--text-muted))]">{b.entry_count}</td>
                          <td className="px-3 py-2 text-center">
                            {b.is_enabled && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">ENABLED</Badge>}
                            {b.is_orphan && <Badge className="text-[8px] bg-red-500/15 text-red-400 border-red-500/30">ORPHAN</Badge>}
                            {b.is_legacy && <Badge className="text-[8px] bg-purple-500/15 text-purple-400 border-purple-500/30">LEGACY</Badge>}
                            {b.mirror_of && (
                              <Badge className="text-[8px] bg-blue-500/15 text-blue-400 border-blue-500/30"
                                title={`Salinan bentuk lama bagi "${b.mirror_of}" — angka sama, bukan broker tambahan`}>
                                MIRROR · {b.mirror_of}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {m.brokers.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-4 text-center text-[hsl(var(--text-muted))] text-xs">No broker data for this month</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Cleanup button */}
            <div className="flex items-center justify-end mt-4 gap-2">
              <span className="text-[11px] text-[hsl(var(--text-muted))]">
                Selected: <strong className="text-white">{Object.values(selected).filter(Boolean).length}</strong> broker entries
              </span>
              <Button onClick={runCleanup} disabled={cleaning || Object.values(selected).filter(Boolean).length === 0}
                className="bg-[#E63946] hover:bg-[#c62828] text-xs" data-testid="run-cleanup-btn">
                {cleaning ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Trash2 size={12} className="mr-1.5" />}
                Remove Selected
              </Button>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-start gap-2">
            <Info size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#93C5FD]">
              Lepas cleanup, refresh page Yearly Snapshot / Overall Performance / War Room untuk tengok new totals.
              Audit log saved ke <code className="text-white">security_logs</code> collection.
            </p>
          </div>
        </>
      )}

      {/* ──────────────  SLOT ARCHIVE / UNDO RENAME (REVERSIBLE) ────────────── */}
      <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]" data-testid="slot-archive-section">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
          <Layers size={15} className="text-[#3B82F6]" />
          Slot Archive & Undo Rename (boleh undo — data TIDAK dipadam)
        </h3>
        <p className="text-[11px] text-[hsl(var(--text-muted))] max-w-3xl leading-relaxed mb-3">
          Archive: pindahkan data slot lama (brokerA–E) ke <code className="text-white">archived_brokerX</code> — slot jadi kosong
          di Daily Data tapi setiap angka masih dalam DB dan boleh dibawa balik bila-bila. Undo Rename: pindahkan data
          dari satu nama broker ke nama lain (contoh data yang tersalah label sebagai VT Market balik ke Moneta).
        </p>

        {/* Month audit & fix */}
        <div className="p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] mb-3">
          <p className="text-[11px] text-[hsl(var(--text-muted))] mb-2 leading-relaxed">
            <span className="text-white font-bold">Audit Bulan:</span> cari entry yang tersimpan dalam dokumen bulan
            yang salah (contoh baris Februari duduk dalam dokumen Januari) — itu sebab Daily Data nampak kosong
            tapi Yearly Snapshot masih ada angka. "Betulkan" hanya PINDAH entry ke bulan betul; tiada apa dipadam
            dan snapshot penuh disimpan.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb || slotBusy}
              onClick={runMonthAudit} data-testid="month-audit-btn">
              <Search size={12} className="mr-1" /> Audit Bulan
            </Button>
            <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb || slotBusy}
              onClick={() => runMonthFix(true)} data-testid="month-fix-scan-btn">
              Betulkan (Dry Run)
            </Button>
            <Button size="sm" className="text-xs bg-[#3B82F6] hover:bg-[#2563eb]"
              disabled={!monthFix || monthFix.moves === 0 || slotBusy}
              onClick={() => runMonthFix(false)} data-testid="month-fix-run-btn">
              Betulkan Sekarang
            </Button>
            <Button size="sm" variant="outline" className="text-xs ml-auto" disabled={!selectedIb || slotBusy}
              onClick={fetchDataHistory} data-testid="data-history-btn">
              Sejarah Import / Recovery
            </Button>
          </div>
          <label className="flex items-center gap-1.5 mt-2 text-[11px] cursor-pointer text-[hsl(var(--text-muted))] w-fit">
            <input type="checkbox" checked={hideMirrors} onChange={(e) => setHideMirrors(e.target.checked)}
              data-testid="hide-mirrors-toggle" />
            Sembunyikan salinan (MIRROR) dalam senarai broker setiap bulan
          </label>

          {monthAudit && (
            <div className="mt-2 space-y-1 text-[11px]" data-testid="month-audit-result">
              <div className="text-white font-bold">
                {monthAudit.total_misplaced_entries} entries berada dalam dokumen bulan yang salah
              </div>
              {monthAudit.months.map((m) => (
                <div key={m.month} className="font-mono text-[hsl(var(--text-muted))]">
                  {m.month}: {m.entries} entries (${fmt(m.commission_usd)})
                  {m.misplaced_entries > 0 && (
                    <span className="text-[#EF4444]"> · {m.misplaced.map(x => `${x.entries} entries milik ${x.target_month} ($${fmt(x.commission_usd)})`).join(', ')}</span>
                  )}
                  {m.conflicts.length > 0 && <span className="text-amber-400"> · {m.conflicts.length} tarikh bertindih</span>}
                  {m.recovered && <span className="text-purple-400"> · dari Data Recovery</span>}
                </div>
              ))}
              {(monthAudit.empty_months || []).length > 0 && (
                <div className="text-amber-400">Bulan kosong: {monthAudit.empty_months.join(', ')}</div>
              )}
              {(monthAudit.months_without_document || []).length > 0 && (
                <div className="text-amber-400">Tiada dokumen: {monthAudit.months_without_document.join(', ')}</div>
              )}
            </div>
          )}

          {monthFix && (
            <div className="mt-2 text-[11px]" data-testid="month-fix-preview">
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 mr-2">DRY RUN</Badge>
              <span className="text-white font-bold">{monthFix.moves}</span>
              <span className="text-[hsl(var(--text-muted))]"> entries akan dipindah </span>
              <span className="text-[hsl(var(--text-muted))] font-mono">
                {Object.entries(monthFix.into_month || {}).map(([m, n]) => `${m} (+${n})`).join(', ')}
              </span>
              {(monthFix.skipped || []).length > 0 && (
                <span className="text-amber-400"> · {monthFix.skipped.length} dilangkau (tarikh dah ada)</span>
              )}
            </div>
          )}

          {dataHistory.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="data-history-list">
              {dataHistory.map((h, i) => (
                <div key={i} className="text-[11px] font-mono text-[hsl(var(--text-muted))]">
                  {String(h.at).slice(0, 16).replace('T', ' ')} · <span className="text-white">{h.kind}</span>
                  {' '}· {(h.months || []).join(', ')} · {h.detail}{h.actor ? ` · ${h.actor}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sync entries (align both storage shapes) */}
        <div className="p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] mb-3">
          <p className="text-[11px] text-[hsl(var(--text-muted))] mb-2 leading-relaxed">
            <span className="text-white font-bold">Selaraskan Data Broker:</span> pastikan angka yang team keyin di Daily Data
            sama dengan Channel Overview, Yearly Snapshot & War Room. Tiada data dipadam — hanya diselaraskan
            (snapshot penuh disimpan sebelum tulis).
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb || slotBusy}
              onClick={() => runSyncEntries(true)} data-testid="sync-scan-btn">
              <Search size={12} className="mr-1" /> Scan (Dry Run)
            </Button>
            <Button size="sm" className="text-xs bg-[#3B82F6] hover:bg-[#2563eb]"
              disabled={!syncPreview || syncPreview.total_entries_changed === 0 || slotBusy}
              onClick={() => runSyncEntries(false)} data-testid="sync-run-btn">
              Selaraskan Sekarang
            </Button>
            <Button size="sm" className="text-xs bg-[#E63946] hover:bg-[#c62828] ml-auto"
              disabled={!selectedIb || slotBusy}
              onClick={() => undoLastBatch('sync_entries')} data-testid="sync-undo-btn">
              Undo Sync Terakhir
            </Button>
            <Button size="sm" className="text-xs bg-[#7c2d12] hover:bg-[#9a3412]"
              disabled={!selectedIb || slotBusy}
              onClick={restoreOriginal} data-testid="restore-original-btn">
              Pulangkan Ke Keadaan Asal
            </Button>
            <Button size="sm" variant="outline" className="text-xs"
              disabled={!selectedIb || slotBusy} onClick={fetchBatches} data-testid="batches-btn">
              Senarai Snapshot
            </Button>
          </div>
          {batches.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="batches-list">
              {batches.map((b) => (
                <div key={b.batch_id} className="flex items-center gap-2 text-[11px] font-mono">
                  <span className="text-[hsl(var(--text-muted))]">{String(b.at).slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-white">{b.operation}</span>
                  <span className="text-[hsl(var(--text-muted))]">{b.months.join(', ')}</span>
                  <Button size="sm" variant="outline" className="h-6 text-[9px] ml-auto"
                    onClick={() => restoreSpecificBatch(b)} data-testid={`restore-batch-${b.batch_id}`}>
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
          {syncPreview && (
            <div className="mt-2 text-[11px] space-y-1" data-testid="sync-preview">
              <div>
                <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 mr-2">DRY RUN</Badge>
                <span className="text-white font-bold">{syncPreview.total_entries_changed}</span>
                <span className="text-[hsl(var(--text-muted))]"> entries dalam {syncPreview.months_affected} bulan perlu diselaraskan</span>
              </div>
              {(syncPreview.preview || []).map((p) => (
                <div key={p.month} className="text-[hsl(var(--text-muted))] font-mono">
                  {p.month}: ${fmt(p.commission_before)} → <span className="text-white">${fmt(p.commission_after)}</span> ({p.entries_changed} entries)
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Slot archive */}
        <div className="p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Slot:</span>
            <Select value={archiveSlot} onValueChange={setArchiveSlot}>
              <SelectTrigger className="w-[140px] h-8 text-xs bg-[hsl(var(--surface-elevated))]" data-testid="archive-slot-select">
                <SelectValue placeholder="Pilih slot" />
              </SelectTrigger>
              <SelectContent>
                {['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb || !archiveSlot || slotBusy}
              onClick={() => runSlotArchive(true)} data-testid="archive-scan-btn">
              <Search size={12} className="mr-1" /> Scan (Dry Run)
            </Button>
            <Button size="sm" className="text-xs bg-[#E63946] hover:bg-[#c62828]" disabled={!archivePreview || archivePreview.total_entries === 0 || slotBusy}
              onClick={() => runSlotArchive(false)} data-testid="archive-run-btn">
              Archive Slot
            </Button>
            <Button size="sm" variant="outline" className="text-xs ml-auto" disabled={!selectedIb || !archiveSlot || slotBusy}
              onClick={runSlotUnarchive} data-testid="unarchive-btn">
              Undo Archive
            </Button>
          </div>
          {archivePreview && (
            <div className="mt-2 text-[11px]" data-testid="archive-preview">
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 mr-2">DRY RUN</Badge>
              <span className="text-white font-bold">{archivePreview.total_entries}</span>
              <span className="text-[hsl(var(--text-muted))]"> entries · </span>
              <span className="text-white font-mono">${fmt(archivePreview.total_commission_usd)}</span>
              <span className="text-[hsl(var(--text-muted))]"> akan dipindahkan ke archived_{archivePreview.slot} — </span>
              <span className="text-[hsl(var(--text-muted))]">{(archivePreview.preview || []).map(p => `${p.month} (${p.entries})`).join(', ')}</span>
            </div>
          )}
        </div>

        {/* Key rename */}
        <div className="p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Dari nama:</span>
            <input value={renameFrom} onChange={(e) => setRenameFrom(e.target.value)} placeholder="vt market"
              className="h-8 px-2 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-xs text-white w-[160px]"
              data-testid="rename-from-input" />
            <span className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Ke nama:</span>
            <input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} placeholder="moneta"
              className="h-8 px-2 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-xs text-white w-[160px]"
              data-testid="rename-to-input" />
            <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb || !renameFrom || !renameTo || slotBusy}
              onClick={() => runKeyRename(true)} data-testid="rename-scan-btn">
              <Search size={12} className="mr-1" /> Scan (Dry Run)
            </Button>
            <Button size="sm" className="text-xs bg-[#E63946] hover:bg-[#c62828]" disabled={!renamePreview || renamePreview.total_entries === 0 || slotBusy}
              onClick={() => runKeyRename(false)} data-testid="rename-run-btn">
              Pindahkan Data
            </Button>
          </div>
          {renamePreview && (
            <div className="mt-2 text-[11px]" data-testid="rename-preview">
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 mr-2">DRY RUN</Badge>
              <span className="text-white font-bold">{renamePreview.total_entries}</span>
              <span className="text-[hsl(var(--text-muted))]"> entries · </span>
              <span className="text-white font-mono">${fmt(renamePreview.total_commission_usd)}</span>
              <span className="text-[hsl(var(--text-muted))]"> · {renamePreview.from_key} → {renamePreview.to_key}</span>
            </div>
          )}
        </div>

        {/* Config rename history */}
        <div className="mt-3">
          <Button size="sm" variant="outline" className="text-xs" disabled={!selectedIb}
            onClick={fetchConfigHistory} data-testid="config-history-btn">
            Sejarah Tukar Nama Broker
          </Button>
          {configHistory.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="config-history-list">
              {configHistory.map((h, i) => (
                <div key={i} className="text-[11px] p-2 rounded bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                  <span className="text-[hsl(var(--text-muted))]">{new Date(h.at).toLocaleString()} · {h.actor} — </span>
                  {h.diffs.map((d, j) => (
                    <span key={j} className="text-white font-mono mr-2">{d.slot}: {d.old} → {d.new}</span>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ──────────────  DAILY DATA DEDUP CLEANUP ────────────── */}
      <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
              <Layers size={15} className="text-amber-400" />
              Daily Data Dedup Cleanup
            </h3>
            <p className="text-[11px] text-[hsl(var(--text-muted))] max-w-2xl leading-relaxed">
              Scan & padam duplicate entries (same date) dari <code className="text-white">daily_data_2026</code>.
              Punca utama inflated commission/lot di Yearly Snapshot, Overall Performance, Channel Overview.
              Default: keep entry paling lengkap (richest data) per tarikh.
            </p>
            {selectedIb && (
              <p className="text-[10px] text-amber-400 mt-1">
                Scoped to: <span className="font-mono">{ibs.find(x => x.id === selectedIb)?.name}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={() => runDedup(true)} disabled={dedupRunning} variant="outline" size="sm" data-testid="dedup-scan-btn">
              {dedupRunning ? <Loader2 size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />}
              Scan (Dry Run)
            </Button>
            <Button onClick={() => {
              if (window.confirm('Run REAL cleanup? Duplicates will be permanently removed from DB. (Owner only.)')) {
                runDedup(false);
              }
            }} disabled={dedupRunning || !dedupResult || dedupResult.total_duplicate_entries_removed === 0}
              className="bg-red-600 hover:bg-red-700" size="sm" data-testid="dedup-run-btn">
              <Trash2 size={12} className="mr-1" />
              Run Cleanup
            </Button>
          </div>
        </div>

        {dedupResult && (
          <div className="rounded-lg border border-[hsl(var(--border))] p-3 bg-[hsl(var(--surface))]">
            <div className="flex items-center gap-3 mb-2 text-[12px] flex-wrap">
              <Badge className={dedupResult.dry_run ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'}>
                {dedupResult.dry_run ? 'DRY RUN' : 'COMMITTED'}
              </Badge>
              <span className="text-white font-bold">{dedupResult.total_duplicate_entries_removed}</span>
              <span className="text-[hsl(var(--text-muted))]">in-array dupes across</span>
              <span className="text-white font-bold">{dedupResult.modified_docs}</span>
              <span className="text-[hsl(var(--text-muted))]">docs</span>
              {dedupResult.duplicate_docs_removed > 0 && (
                <>
                  <span className="text-[hsl(var(--text-muted))]">·</span>
                  <Badge className="bg-red-500/15 text-red-400 border-red-500/30">
                    {dedupResult.duplicate_docs_removed} duplicate DOCUMENTS detected
                  </Badge>
                </>
              )}
            </div>

            {/* Duplicate documents section */}
            {dedupResult.duplicate_docs_summary && dedupResult.duplicate_docs_summary.length > 0 && (
              <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/5 p-2">
                <p className="text-[11px] font-bold text-red-400 mb-1.5">
                  ⚠️ Duplicate DOCUMENTS (root cause of 2× / 3× inflation):
                </p>
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
                    <tr>
                      <th className="text-left py-1">IB</th>
                      <th className="text-left py-1">Month</th>
                      <th className="text-right py-1">Extra Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dedupResult.duplicate_docs_summary.map((s, i) => (
                      <tr key={i} className="border-t border-red-500/20">
                        <td className="py-1 font-mono text-white">{ibs.find(x => x.id === s.ib_id)?.name || s.ib_id}</td>
                        <td className="py-1">{s.month}</td>
                        <td className="py-1 text-right font-mono text-red-400 font-bold">+{s.duplicate_docs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {dedupResult.summary && dedupResult.summary.length > 0 && (
              <div className="overflow-x-auto max-h-[280px] overflow-y-auto rounded-md border border-[hsl(var(--border))]">
                <table className="w-full text-[11px]">
                  <thead className="bg-[hsl(var(--card))] text-[hsl(var(--text-muted))] text-[10px] uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5">IB</th>
                      <th className="text-left px-3 py-1.5">Month</th>
                      <th className="text-right px-3 py-1.5">Marketing</th>
                      <th className="text-right px-3 py-1.5">Sales</th>
                      <th className="text-right px-3 py-1.5">Operation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dedupResult.summary.map((s, i) => (
                      <tr key={i} className="border-t border-[hsl(var(--border))]">
                        <td className="px-3 py-1.5 font-mono text-white">{ibs.find(x => x.id === s.ib_id)?.name || s.ib_id}</td>
                        <td className="px-3 py-1.5">{s.month}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.marketing_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.marketing_removed}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.sales_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.sales_removed}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{color: s.operation_removed > 0 ? '#f59e0b' : 'inherit'}}>{s.operation_removed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
